import React from "react";
import { useTranslation } from "react-i18next";
import { IWhatsNewItem } from "api/whatsNew/whatsNew.type";
import { cn } from "utils/cn";

interface ReleaseNotesCardProps {
  items: IWhatsNewItem[];
  /** Newest member's `published_at` — rendered as the card's date. */
  publishedAt: string;
  className?: string;
}

// The card's calendar day in UTC — the same day the grouping keys on, so there is exactly one card per label.
const formatReleaseDate = (iso: string, locale: string): string => {
  const ts = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(ts)) return "";

  try {
    return new Intl.DateTimeFormat(locale || "en", { dateStyle: "medium", timeZone: "UTC" }).format(ts);
  } catch {
    return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeZone: "UTC" }).format(ts);
  }
};

// REF-6550: one card per UTC day listing its improvements and bug fixes, each with a kind badge. Used by
// the What's New panel and the See-all page. Informational only — these rows carry no modal content and
// a day's card has no single destination, so neither the card nor its lines are clickable.
const ReleaseNotesCard = ({ items, publishedAt, className }: ReleaseNotesCardProps) => {
  const { t, i18n } = useTranslation();
  const releaseDate = formatReleaseDate(publishedAt, i18n.language);

  return (
    <div className={cn("flex w-full flex-col gap-2.5 rounded-xl px-3 py-3 text-left", className)} data-release-notes-card>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[14px] font-semibold text-trueBlack dark:text-white">{t("common:whats_new.improvements_and_fixes")}</span>
        {releaseDate && <span className="flex-shrink-0 text-[12px] text-grey/80">{releaseDate}</span>}
      </div>

      {/* Two-column grid so every title starts at the widest badge's edge, whatever the locale's label length. */}
      <ul className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-2 gap-y-2">
        {items.map((item) => {
          const isFix = item.kind === "bug_fix";

          return (
            <li key={item.id} className="contents">
              <span
                className={cn(
                  "mt-px self-start justify-self-start rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-4",
                  isFix ? "bg-orange/15 text-[#B4532A] dark:text-orange" : "bg-purpleGreen/10 text-purpleGreen"
                )}
              >
                {isFix ? t("common:whats_new.badges.fix") : t("common:whats_new.badges.improvement")}
              </span>
              <div className="flex min-w-0 flex-col gap-0.5">
                {/* `overflow-wrap:anywhere` so an unbroken token wraps instead of widening the column; the
                    title clamps like the description, with the full text on hover. */}
                <span className="line-clamp-2 text-[13px] font-medium leading-5 text-trueBlack [overflow-wrap:anywhere] dark:text-white" title={item.title}>
                  {item.title}
                </span>
                {item.description && <span className="custom-line-clamp-2 text-[12px] text-grey [overflow-wrap:anywhere]">{item.description}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ReleaseNotesCard;
